import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, delay } from 'rxjs/operators';
import { of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  closeSchedulesModal,
  duplicateSchedule,
  getSchedulesList,
  getSchedulesListError,
  getSchedulesListResponse,
  loadMoreSchedulesList,
  loadMoreSchedulesListResponse,
  removeSchedule,
  removeScheduleError,
  removeScheduleItem,
  removeScheduleResponse,
  runSchedule,
  saveSchedule,
  saveScheduleError,
  saveScheduleResponse,
  searchSchedulesList,
  searchSchedulesListResponse,
  updateSchedule,
  updateScheduleError,
  updateScheduleResponse,
} from './schedules.actions';
import { SchedulesResource } from '../resources/schedules.resource';
import { SLIDER_CLOSE_ANIMATION_DURATION } from '../../constants/animation-constants';
import { NotifyService } from '../../common/services/notify.service';

@Injectable()
export class SchedulesEffects {
  @Effect()
  getSchedules$ = this.actions$.pipe(
    ofType(getSchedulesList),
    switchMap(({ params }) =>
      this.schedulesResource.query(params).pipe(
        map((schedules) => getSchedulesListResponse({ schedules })),
        catchError((error) => {
          this.notify.error(this.translate.instant(`response.${error.status}.message`));
          return of(getSchedulesListError({ error }));
        }),
      ),
    ),
  );

  @Effect()
  searchSchedules$ = this.actions$.pipe(
    ofType(searchSchedulesList),
    switchMap(({ params }) =>
      this.schedulesResource.search(params).pipe(
        map((schedules) => searchSchedulesListResponse({ schedules })),
        catchError((error) => {
          this.notify.error(this.translate.instant(`response.${error.status}.message`));
          return of(getSchedulesListError({ error }));
        }),
      ),
    ),
  );

  @Effect()
  loadMoreSchedules$ = this.actions$.pipe(
    ofType(loadMoreSchedulesList),
    switchMap(({ params }) =>
      this.schedulesResource.query(params).pipe(
        map((schedules) => loadMoreSchedulesListResponse({ schedules })),
        catchError((error) => {
          this.notify.error(this.translate.instant(`response.${error.status}.message`));
          return of(getSchedulesListError({ error }));
        }),
      ),
    ),
  );

  @Effect()
  saveSchedule$ = this.actions$.pipe(
    ofType(saveSchedule),
    switchMap(({ schedule }) =>
      this.schedulesResource.save(schedule, { include: 'owner,package,workspace' } as any).pipe(
        map((scheduleResponse) => saveScheduleResponse({ data: scheduleResponse })),
        catchError((response) => of(saveScheduleError({ error: response.error }))),
      ),
    ),
  );

  @Effect()
  saveScheduleResponse$ = this.actions$.pipe(
    ofType(saveScheduleResponse),
    map(() => closeSchedulesModal()),
  );

  @Effect()
  duplicateSchedule$ = this.actions$.pipe(
    ofType(duplicateSchedule),
    switchMap(({ schedule }) =>
      this.schedulesResource.clone(schedule.id, { ...schedule }, { include: 'owner,package,workspace' } as any).pipe(
        map((scheduleResponse) => saveScheduleResponse({ data: scheduleResponse })),
        catchError((response) => of(saveScheduleError({ error: response.error }))),
      ),
    ),
  );

  @Effect()
  runSchedule$ = this.actions$.pipe(
    ofType(runSchedule),
    switchMap(({ schedule }) =>
      this.schedulesResource.run(schedule.id, { ...schedule }, { include: 'owner,package,workspace' } as any).pipe(
        map((scheduleResponse) => {
          const message = this.translate.instant('schedule.notifications.submitted.message', { name: schedule.name });

          this.notify.success(message);

          return updateScheduleResponse({ data: scheduleResponse });
        }),
        catchError((response) => of(updateScheduleError({ error: response.error }))),
      ),
    ),
  );

  @Effect()
  updateSchedule$ = this.actions$.pipe(
    ofType(updateSchedule),
    switchMap(({ schedule, params, scheduleId }) =>
      this.schedulesResource
        .update(scheduleId, schedule, { ...params, include: 'owner,package,workspace' } as any)
        .pipe(
          map((scheduleResponse) => updateScheduleResponse({ data: scheduleResponse })),
          catchError((response) => of(updateScheduleError({ error: response.error }))),
        ),
    ),
  );

  @Effect()
  updateScheduleResponse = this.actions$.pipe(
    ofType(updateScheduleResponse),
    map(() => closeSchedulesModal()),
  );

  @Effect()
  removeSchedule$ = this.actions$.pipe(
    ofType(removeSchedule),
    switchMap(({ scheduleId }) =>
      this.schedulesResource.remove(scheduleId).pipe(
        map((scheduleResponse) => removeScheduleItem({ data: scheduleResponse })),
        catchError((response) => of(removeScheduleError({ error: response.error }))),
      ),
    ),
  );

  @Effect()
  removeScheduleResponse$ = this.actions$.pipe(
    ofType(removeScheduleItem),
    delay(SLIDER_CLOSE_ANIMATION_DURATION),
    map(({ data }) => removeScheduleResponse({ data })),
  );

  constructor(
    private actions$: Actions,
    private schedulesResource: SchedulesResource,
    private translate: TranslateService,
    private notify: NotifyService,
  ) {}
}
